import { ref } from "vue";
import type { OssArchiveRejectError } from "./oss-archive-error";
import {
	OSS_ARCHIVE_PARENT_MESSAGE_TYPE,
	isOssArchiveEmbeddedFrame,
	mapErrorCodeToDialogState,
	normalizeOssArchiveError
} from "./oss-archive-error";
import type { OssArchiveUiCopy } from "./oss-archive-dialog";
import { dispatchOssArchiveError, getOssArchiveConfigSnapshot, subscribeOssArchiveError } from "./oss-archive-integration";

export interface OssArchiveRestoreDialogPayload {
	error?: OssArchiveRejectError;
	pid?: number;
	orderId?: number;
	objectKeys?: string[];
	restoreStatus?: string;
	uiCopy?: OssArchiveUiCopy;
	onReady?: () => void;
}

export { isOssArchiveEmbeddedFrame };

export const ossArchiveRestoreDialogVisible = ref(false);
export const ossArchiveRestoreDialogPayload = ref<OssArchiveRestoreDialogPayload | null>(null);
/** 确认窗打开时挡住预览；关闭后必须清掉，避免 preview_three 整页黑遮罩残留 */
export const ossArchivePreviewBlocked = ref(false);

let dismissedPid: number | undefined;
let dismissedWhilePending = false;

function payloadDialogState(payload: OssArchiveRestoreDialogPayload): string {
	const errorCode = payload.error?.error_code || payload.error?.data?.error_code || "";
	const restoreStatus = payload.restoreStatus || payload.error?.data?.restore_status;
	return mapErrorCodeToDialogState(String(errorCode), restoreStatus ? String(restoreStatus) : undefined);
}

function shouldIgnoreReopen(payload: OssArchiveRestoreDialogPayload): boolean {
	if (!dismissedWhilePending) {
		return false;
	}
	const incomingPid = payload.pid || payload.error?.data?.pid;
	const samePid = !dismissedPid || !incomingPid || Number(dismissedPid) === Number(incomingPid);
	if (!samePid) {
		return false;
	}
	const state = payloadDialogState(payload);
	// 办理中关闭后不再立刻弹出授权窗（iframe 会重试 need）；办理中提示允许再次点开
	return state === "need";
}

function postRestoreToParent(error: unknown) {
	if (!isOssArchiveEmbeddedFrame()) {
		return;
	}
	window.parent.postMessage(
		{
			type: OSS_ARCHIVE_PARENT_MESSAGE_TYPE,
			error
		},
		"*"
	);
}

export function openOssArchiveRestoreDialog(payload: OssArchiveRestoreDialogPayload) {
	if (isOssArchiveEmbeddedFrame()) {
		postRestoreToParent(
			payload.error || {
				error_code: "OSS_ARCHIVED_NEED_RESTORE",
				data: {
					pid: payload.pid,
					order_id: payload.orderId,
					object_keys: payload.objectKeys,
					restore_status: payload.restoreStatus
				}
			}
		);
		return;
	}
	if (shouldIgnoreReopen(payload)) {
		return;
	}
	ossArchiveRestoreDialogPayload.value = payload;
	ossArchiveRestoreDialogVisible.value = true;
	const state = payloadDialogState(payload);
	if (state === "need" || state === "pending" || state === "expired" || state === "failed") {
		ossArchivePreviewBlocked.value = true;
	}
}

export function closeOssArchiveRestoreDialog() {
	const payload = ossArchiveRestoreDialogPayload.value;
	const state = payload ? payloadDialogState(payload) : "";
	if (state === "pending") {
		dismissedWhilePending = true;
		dismissedPid = payload?.pid || payload?.error?.data?.pid;
	}
	if (state === "ready") {
		dismissedWhilePending = false;
		dismissedPid = undefined;
	}
	ossArchivePreviewBlocked.value = false;
	ossArchiveRestoreDialogVisible.value = false;
	ossArchiveRestoreDialogPayload.value = null;
}

export function resetOssArchiveRestoreSession() {
	ossArchiveRestoreDialogVisible.value = false;
	ossArchiveRestoreDialogPayload.value = null;
	ossArchivePreviewBlocked.value = false;
	dismissedPid = undefined;
	dismissedWhilePending = false;
}

function payloadFromArchiveError(error: OssArchiveRejectError): OssArchiveRestoreDialogPayload {
	return {
		error,
		pid: error.data?.pid,
		orderId: error.data?.order_id,
		objectKeys: error.data?.object_keys,
		restoreStatus: error.data?.restore_status,
		uiCopy: getOssArchiveConfigSnapshot()?.uiCopy
	};
}

/**
 * 看图 catch 兜底：后端已返回归档码时，关闸/无订阅也不能把确认窗吞掉。
 * iframe 内只通知父页，主页面只弹一个确认窗。
 */
export function ensureOssArchiveRestoreDialog(error: unknown): boolean {
	if (ossArchiveRestoreDialogVisible.value) {
		return true;
	}
	const opened = dispatchOssArchiveError(error);
	if (opened && ossArchiveRestoreDialogVisible.value) {
		return true;
	}
	const normalized = opened || normalizeOssArchiveError(error);
	if (!normalized) {
		return Boolean(opened);
	}
	openOssArchiveRestoreDialog(payloadFromArchiveError(normalized));
	return true;
}

function isParentRestoreMessage(data: unknown): data is { error: OssArchiveRejectError } {
	if (!data || typeof data !== "object") {
		return false;
	}
	const msg = data as { type?: unknown; error?: unknown };
	return (
		msg.type === OSS_ARCHIVE_PARENT_MESSAGE_TYPE && !!msg.error && typeof msg.error === "object"
	);
}

export function initOssArchiveErrorHandler() {
	const stopSub = subscribeOssArchiveError((error) => {
		openOssArchiveRestoreDialog(payloadFromArchiveError(error));
	});
	const onMessage = (event: MessageEvent) => {
		if (!isParentRestoreMessage(event.data)) {
			return;
		}
		openOssArchiveRestoreDialog(payloadFromArchiveError(event.data.error));
	};
	window.addEventListener("message", onMessage);
	return () => {
		stopSub();
		window.removeEventListener("message", onMessage);
	};
}
